import type { PreferenceStorage } from '../audio/sound-store';

const KEYS = {
  showFps: 'showFps',
  confirmLeave: 'confirmLeave',
  idleMenu: 'idleMenu',
  skipIntro: 'skipIntro',
} as const;

export type SettingName = keyof typeof KEYS;

export interface SettingsStoreOptions {
  readonly storage?: PreferenceStorage | null | undefined;
}

const DEFAULTS: Readonly<Record<SettingName, boolean>> = {
  showFps: false,
  confirmLeave: true,
  idleMenu: true,
  skipIntro: false,
};

function defaultStorage(): PreferenceStorage | null {
  try {
    return globalThis.localStorage;
  } catch {
    return null;
  }
}

export class SettingsStore {
  private readonly storage: PreferenceStorage | null;
  private readonly values: Record<SettingName, boolean>;
  private readonly listeners = new Set<() => void>();

  constructor(options: SettingsStoreOptions = {}) {
    this.storage = options.storage === undefined ? defaultStorage() : options.storage;
    this.values = { ...DEFAULTS };
    for (const name of Object.keys(DEFAULTS) as SettingName[]) {
      const stored = this.read(KEYS[name]);
      if (stored === '0' || stored === '1') this.values[name] = stored === '1';
    }
  }

  get(name: SettingName): boolean {
    return this.values[name];
  }

  set(name: SettingName, value: boolean): void {
    if (this.values[name] === value) return;
    this.values[name] = value;
    this.write(KEYS[name], value ? '1' : '0');
    for (const listener of this.listeners) listener();
  }

  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  private read(key: string): string | null {
    try {
      return this.storage?.getItem(key) ?? null;
    } catch {
      return null;
    }
  }

  private write(key: string, value: string): void {
    try {
      this.storage?.setItem(key, value);
    } catch {
      // must not break the toggle the player just clicked
    }
  }
}
